import { Link } from "react-router-dom";
import {
  ArrowRight,
  Sparkles,
  Heart,
  AlertCircle,
  Target,
  Users,
  Quote,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";

/**
 * About page — founder-led, professional, modern.
 *
 * Structured for fast iteration with founder copy. The headlines and
 * placeholder body text below are intentionally written in a credible,
 * non-fluffy register that can be replaced inline without touching the
 * layout. Visual structure (sections, hierarchy, spacing) is locked in.
 */
export default function AboutPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white" data-testid="about-page">
      <SiteHeader />

      {/* Hero */}
      <section className="pt-16 pb-12 lg:pt-24 lg:pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="mb-6 bg-blue-100 text-blue-800 hover:bg-blue-200">
            <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Our story
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
            Built so applicants get<span className="text-blue-600"> a real shot</span> at being seen.
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
            Inter-View is a Canadian product helping job seekers introduce themselves the way
            people actually decide who to hire — beyond a resume that ends up in a pile.
          </p>
        </div>
      </section>

      {/* Founder story */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4">
              <div className="sticky top-24">
                <Badge variant="outline" className="border-blue-200 text-blue-700 mb-3">
                  <Heart className="h-3.5 w-3.5 mr-1.5" /> Founder note
                </Badge>
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 leading-snug">
                  Why we built Inter-View
                </h2>
              </div>
            </div>
            <div className="lg:col-span-8 space-y-5 text-gray-700 leading-relaxed text-lg">
              <Quote className="h-8 w-8 text-blue-200" />
              <p>
                I started Inter-View because I believe too many capable people are overlooked
                in the hiring process, not because they lack work ethic or personality, but
                because traditional applications do a poor job of showing who they really are.
              </p>
              <p>
                A resume and cover letter can share qualifications, but they cannot capture
                presence, communication, confidence, or attitude. In many industries — especially
                customer-facing ones — those qualities matter just as much as experience.
              </p>
              <p>
                I wanted to create a tool that helps applicants present themselves in a more
                complete and professional way. Inter-View combines a resume, a cover letter,
                and a short video introduction into one package, allowing employers to learn
                more about a candidate even when a manager or owner is not available in person.
              </p>
              <p>
                My goal with Inter-View is simple: help applicants stand out for the right
                reasons, make stronger first impressions, and give employers a better way to
                identify people with real potential.
              </p>
              <p className="text-gray-900 font-semibold pt-2">
                — Founder, Inter-View
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Problem */}
      <section className="py-16 bg-gradient-to-br from-rose-50/40 via-orange-50/40 to-amber-50/40 border-y border-orange-100/60">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <Badge variant="outline" className="border-rose-200 text-rose-700 mb-3">
              <AlertCircle className="h-3.5 w-3.5 mr-1.5" /> The problem
            </Badge>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Traditional job applications are quietly broken.
            </h2>
            <p className="text-lg text-gray-600">
              A resume is a one-dimensional snapshot. It can't say hello. It can't smile.
              It can't show up. The result is a hiring process that filters out the wrong things.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: "Resumes blur together",
                copy: "By the time a hiring manager finishes a stack of resumes, most candidates are indistinguishable.",
              },
              {
                title: "First impressions never happen",
                copy: "When a manager isn't at the counter, the applicant has no way to make a real first impression.",
              },
              {
                title: "Personality is invisible",
                copy: "The qualities that decide who actually gets hired — energy, fit, communication — don't fit on a page.",
              },
            ].map((item, i) => (
              <Card key={i} className="bg-white/80 backdrop-blur border-orange-100">
                <CardContent className="p-6">
                  <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.copy}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Vision */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <Badge variant="outline" className="border-blue-200 text-blue-700 mb-3">
              <Target className="h-3.5 w-3.5 mr-1.5" /> The vision
            </Badge>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              An application that introduces you, not just describes you.
            </h2>
            <p className="text-lg text-gray-600">
              Inter-View packages everything an employer actually needs into one printable,
              scannable application — so a candidate's first impression happens the moment
              their package is opened.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                badge: "01",
                title: "One package",
                copy: "Resume, cover letter, and a 60-second video, stitched and ready for in-person hand-off.",
              },
              {
                badge: "02",
                title: "Recruiter-ready",
                copy: "A clean QR code on the printed package links straight to the candidate's recruiter page.",
              },
              {
                badge: "03",
                title: "Applicant-owned",
                copy: "Profile is the source of truth — change once, update everywhere automatically.",
              },
              {
                badge: "04",
                title: "Production-grade",
                copy: "Built on durable storage, served from Canadian infrastructure, ready for real volume.",
              },
              {
                badge: "05",
                title: "Field-tested",
                copy: "Designed in close collaboration with hospitality and customer-facing operators.",
              },
              {
                badge: "06",
                title: "Built to grow with you",
                copy: "From first job applications to leadership roles — same package, deeper depth.",
              },
            ].map((item) => (
              <div
                key={item.badge}
                className="group p-6 rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 hover:shadow-md hover:border-blue-200 transition-all"
              >
                <div className="text-xs font-semibold text-blue-600 mb-2 tracking-widest">
                  {item.badge}
                </div>
                <h3 className="font-bold text-gray-900 mb-1.5 text-lg">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed text-sm">{item.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Users className="h-10 w-10 text-white/80 mx-auto mb-4" />
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            Build the application you wish you'd had.
          </h2>
          <p className="text-lg text-blue-100 mb-8 leading-relaxed">
            It takes about ten minutes to put together a complete Inter-View package. Most
            people send a worse application than the one they could have sent today.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/signup">
              <Button
                size="lg"
                className="w-full sm:w-auto bg-white text-blue-700 hover:bg-blue-50 text-base px-8 shadow-lg"
                data-testid="about-cta-signup"
              >
                Get started free
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
            <Link to="/how-it-works">
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto border-white/60 text-white hover:bg-white/10 text-base px-8"
                data-testid="about-cta-how-it-works"
              >
                See how it works
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
